/**
 * Keyed ownership for objects with external resources (view models with subscriptions, sockets): created on
 * first use during render, retained while a mounted component uses it, disposed shortly after the last user
 * unmounts. Safe under StrictMode's mount/unmount/mount and with discarded renders.
 */
import { useEffect, useMemo } from "react";

type Disposable = { dispose: () => void };
type Entry = { value: Disposable; refs: number; timer: ReturnType<typeof setTimeout> | undefined };

const owned = new Map<string, Entry>();
/** Grace period for values created by a render that never mounted. */
const UNMOUNTED_GRACE_MS = 5000;

function schedule(key: string, entry: Entry, ms: number): void {
  if (entry.timer !== undefined) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    entry.timer = undefined;
    if (entry.refs === 0 && owned.get(key) === entry) {
      owned.delete(key);
      entry.value.dispose();
    }
  }, ms);
}

function obtain<T extends Disposable>(key: string, create: () => T): T {
  let entry = owned.get(key);
  if (entry === undefined) {
    entry = { value: create(), refs: 0, timer: undefined };
    owned.set(key, entry);
    schedule(key, entry, UNMOUNTED_GRACE_MS);
  }
  return entry.value as T;
}

export function useOwned<T extends Disposable>(key: string, create: () => T): T {
  const value = useMemo(() => obtain(key, create), [key, create]);
  useEffect(() => {
    const entry = owned.get(key);
    if (entry === undefined) return;
    entry.refs += 1;
    if (entry.timer !== undefined) {
      clearTimeout(entry.timer);
      entry.timer = undefined;
    }
    return () => {
      entry.refs -= 1;
      if (entry.refs === 0) schedule(key, entry, 0);
    };
  }, [key]);
  return value;
}
